import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ModelSelector } from "./ModelSelector";
import { CreativitySelector } from "./CreativitySelector";
import { FileUploader } from "./FileUploader";
import { useVectorStoreFiles } from "./hooks/useVectorStoreFiles";
import { getOpenAIClient } from "@/lib/openai";
import { supabase } from "@/lib/supabase";
import type { AssistantFormData } from "./types";
import { NameField } from "./form/NameField";
import { InstructionsField } from "./form/InstructionsField";
import { SubmitButton } from "./form/SubmitButton";

interface AssistantFormProps {
  onSubmit: (data: AssistantFormData) => Promise<void>;
  isLoading: boolean;
  isEnhancing: boolean;
  instructions: string;
  onInstructionsChange: (instructions: string) => void;
  onEnhanceInstructions: (name: string, instructions: string) => Promise<void>;
  initialValues?: {
    name: string;
    model: string;
    creativity: string;
    assistantId?: string;
  };
}

export const AssistantForm = ({ 
  onSubmit, 
  isLoading, 
  isEnhancing,
  instructions,
  onInstructionsChange,
  onEnhanceInstructions,
  initialValues 
}: AssistantFormProps) => {
  const [name, setName] = useState(initialValues?.name || "");
  const [model, setModel] = useState(initialValues?.model || "gpt-4o-mini");
  const [creativity, setCreativity] = useState(initialValues?.creativity || "neutral");
  const [files, setFiles] = useState<File[]>([]);
  const [vectorStoreId, setVectorStoreId] = useState<string>();

  const { existingFiles, setExistingFiles, isLoading: isLoadingFiles } = useVectorStoreFiles(
    initialValues?.assistantId ? vectorStoreId : undefined
  );

  useEffect(() => {
    const fetchVectorStoreId = async () => {
      if (initialValues?.assistantId) {
        const { data: assistant } = await supabase
          .from('assistants')
          .select('vector_store_id')
          .eq('id', initialValues.assistantId)
          .single();

        if (assistant?.vector_store_id) {
          console.log('Found vector store ID:', assistant.vector_store_id);
          setVectorStoreId(assistant.vector_store_id);
        }
      }
    };

    fetchVectorStoreId();
  }, [initialValues?.assistantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting form with instructions:', instructions);
    await onSubmit({ 
      name, 
      instructions, 
      model, 
      creativity,
      files,
      vectorStoreId
    });
  };

  const handleEnhanceClick = async () => {
    console.log('Enhancing instructions for:', name, 'current instructions:', instructions);
    await onEnhanceInstructions(name, instructions);
  };

  const handleFileError = (message: string) => {
    toast.error(message);
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      const openai = await getOpenAIClient();
      
      const { data: file } = await supabase
        .from('vector_store_files')
        .select('openai_file_id')
        .eq('id', fileId)
        .single();

      if (!file) {
        throw new Error('File not found');
      }

      await openai.files.del(file.openai_file_id);

      const { error: deleteError } = await supabase
        .from('vector_store_files')
        .delete()
        .eq('id', fileId);

      if (deleteError) throw deleteError;

      setExistingFiles(existingFiles.filter(f => f.id !== fileId));
      toast.success("File deleted successfully");
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error("Failed to delete file");
      throw error;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <NameField name={name} onChange={setName} />
      
      <InstructionsField
        instructions={instructions}
        onInstructionsChange={onInstructionsChange}
        onEnhanceInstructions={handleEnhanceClick}
        isEnhancing={isEnhancing}
        name={name}
      />

      <div className="space-y-2">
        <FileUploader
          files={files}
          setFiles={setFiles}
          onFileError={handleFileError}
          isUploading={isLoading}
          existingFiles={existingFiles}
          onDeleteFile={handleDeleteFile}
          isLoading={isLoadingFiles}
          assistantId={initialValues?.assistantId}
          vectorStoreId={vectorStoreId}
          onVectorStoreCreated={setVectorStoreId}
        />
      </div>

      <ModelSelector model={model} onModelChange={setModel} />
      <CreativitySelector value={creativity} onValueChange={setCreativity} />

      <SubmitButton 
        isLoading={isLoading} 
        isEdit={!!initialValues} 
      />
    </form>
  );
};